import { axios } from '@/plugins/axios';
import { DiscountTypesEnum, FulfillmentMethodsEnum } from '@/enums';
import store from '../store';

const base = '/discount-codes';

/**
 * Checks if a discount is valid for the current order context
 *
 * @param {Object} discount - The discount to validate
 * @param {string} fulfillmentMethod - The current fulfillment method
 * @param {number} cartSubtotal - The current cart subtotal
 * @returns {boolean} True if the discount is valid, false otherwise
 */
export function isDiscountValid(discount, fulfillmentMethod, cartSubtotal) {
    if (!discount || !Array.isArray(discount.fulfillmentMethods)) {
        return false;
    }

    // Check if discount applies to the current fulfillment method
    if (!discount.fulfillmentMethods.includes(fulfillmentMethod)) {
        return false;
    }

    if (discount.minimumAmount && cartSubtotal < discount.minimumAmount) {
        return false;
    }

    return true;
}

/**
 * Check if a discount code is valid
 * @param {Object} params - Parameters
 * @param {string} params.venueId - Venue ID
 * @param {string} params.code - Discount code
 * @param {string} params.date - Date for discount
 * @param {string} params.timeSlot - Time slot
 * @param {string} params.fulfillmentMethod - Fulfillment method
 * @param {string} params.email - User email
 * @returns {Promise} - Axios promise
 */
export function checkDiscountCode({
    venueId,
    code,
    date,
    timeSlot,
    fulfillmentMethod,
    email
}) {
    return axios.get(`${base}/check`, {
        params: { venueId, code, date, timeSlot, fulfillmentMethod, email }
    });
}

/**
 * Returns the first auto-applied discount for the current fulfillment method.
 * This is the original implementation that just returns the first applicable discount.
 *
 * @param {Object} rootState - The Vuex root state
 * @param {string} fulfillmentMethod - The current fulfillment method
 * @returns {Object|null} The first applicable discount or null if none available
 */
export function getFirstAutoAppliedDiscount(rootState, fulfillmentMethod) {
    const venue = rootState.venue.venue;
    const autoDiscounts = venue.autoAppliedDiscountCode || [];
    const cartSubtotal = store.getters['cart/cartSubtotal'] || 0;

    const applicableDiscounts = autoDiscounts.filter(discount =>
        isDiscountValid(discount, fulfillmentMethod, cartSubtotal)
    );

    return applicableDiscounts.length > 0 ? applicableDiscounts[0] : null;
}

/**
 * Returns the best auto-applied discount for the current fulfillment method.
 * Evaluates all available auto discounts and returns the one that provides
 * the greatest benefit to the customer.
 *
 * @param {Object} rootState - The Vuex root state
 * @param {string} fulfillmentMethod - The current fulfillment method
 * @returns {Object|null} The best applicable discount or null if none available
 */
export function getBestAutoAppliedDiscount(rootState, fulfillmentMethod) {
    const venue = rootState.venue.venue;
    const autoDiscounts = venue.autoAppliedDiscountCode || [];
    const cartSubtotal = store.getters['cart/cartSubtotal'] || 0;

    const applicableDiscounts = autoDiscounts.filter(discount =>
        isDiscountValid(discount, fulfillmentMethod, cartSubtotal)
    );

    if (applicableDiscounts.length === 0) {
        return null;
    }

    if (applicableDiscounts.length === 1) {
        return applicableDiscounts[0];
    }

    return applicableDiscounts.reduce((bestDiscount, currentDiscount) => {
        if (!bestDiscount) return currentDiscount;

        if (currentDiscount.type === bestDiscount.type) {
            if (
                currentDiscount.type === DiscountTypesEnum.PERCENT ||
                currentDiscount.type === DiscountTypesEnum.BUY_ONE_GET_ONE_FREE
            ) {
                return currentDiscount.codeAmount > bestDiscount.codeAmount
                    ? currentDiscount
                    : bestDiscount;
            } else if (currentDiscount.type === DiscountTypesEnum.FIXED) {
                return currentDiscount.codeAmount > bestDiscount.codeAmount
                    ? currentDiscount
                    : bestDiscount;
            } else if (
                currentDiscount.type === DiscountTypesEnum.FREE_DELIVERY
            ) {
                return bestDiscount;
            }
        }

        const typePriority = {
            [DiscountTypesEnum.FREE_DELIVERY]:
                fulfillmentMethod === FulfillmentMethodsEnum.DELIVERY ? 3 : 0,
            [DiscountTypesEnum.FIXED]: 2,
            [DiscountTypesEnum.PERCENT]: 1,
            [DiscountTypesEnum.BUY_ONE_GET_ONE_FREE]: 0
        };

        const currentPriority = typePriority[currentDiscount.type] || 0;
        const bestPriority = typePriority[bestDiscount.type] || 0;

        if (currentPriority !== bestPriority) {
            return currentPriority > bestPriority
                ? currentDiscount
                : bestDiscount;
        }

        return bestDiscount;
    }, null);
}

export default {
    checkDiscountCode,
    getBestAutoAppliedDiscount,
    getFirstAutoAppliedDiscount,
    isDiscountValid
};
